import {
    useCallback,
    useState,
    useEffect,
    useRef,
    RefObject,
    MouseEvent
} from 'react';
import { toast } from 'react-toastify';

import * as A from '../Auth.style';
import theme from '@styles/theme';
import { onChangeEventType } from '@utils/types';
import { onlyNumber, autofocusHandler } from '@utils/common';
import { validateJoin } from '@utils/validation';

import { FlexBox, RegularFont } from '@components/styled/StyledComponents';
import StyledInput from '@components/styled/StyledInput';
import StyledPassword from '@components/styled/StyledPassword';
import StyledButton from '@components/styled/StyledButton';
import StyledCheckbox from '@components/styled/StyledCheckbox';
import StyledPolicyCheckbox from '@components/styled/StyledPolicyCheckbox';
import StyledPostcode from '@components/styled/StyledPostcode';
import StyledSelect from '@components/styled/StyledSelect';

type Inputs = {
    email: string;
    emailAuthNum: string;
    username: string;
    license: string;
    hospitalName: string;
    password: string;
    passwordConfirm: string;
    phoneNum1: string;
    phoneNum2: string;
    phoneNum3: string;
    faxNum1: string;
    faxNum2: string;
    faxNum3: string;
    address: string;
    zipcode: string;
    addressDetail: string;
    chart: string;
};

export type JoinErrors = {
    [key in keyof Inputs]?: string;
};

const Join = () => {
    // 번호 input autofocus
    const phoneRef1 = useRef<HTMLInputElement>(null);
    const phoneRef2 = useRef<HTMLInputElement>(null);
    const faxRef1 = useRef<HTMLInputElement>(null);
    const faxRef2 = useRef<HTMLInputElement>(null);
    // 번호 여부 체크박스
    const [hasPhoneNum, setHasPhoneNum] = useState(false);
    const [hasFaxNum, setHasFaxNum] = useState(false);
    const [errors, setErrors] = useState<JoinErrors>({});
    const [checkOverlap, setCheckOverlap] = useState<Record<string, boolean>>({
        email: false,
        emailAuthNum: false,
        license: false
    });
    const [togglePolicy, setTogglePolicy] = useState<Record<string, boolean>>({
        checkAll: false,
        privacy: false,
        service: false
    });
    const [inputs, setInputs] = useState<Record<string, string>>({
        email: '',
        emailAuthNum: '',
        username: '',
        license: '',
        hospitalName: '',
        password: '',
        passwordConfirm: '',
        phoneNum1: '',
        phoneNum2: '',
        phoneNum3: '',
        faxNum1: '',
        faxNum2: '',
        faxNum3: '',
        address: '',
        zipcode: '',
        addressDetail: '',
        chart: '차트 선택'
    });

    // 없음 선택 시 전화번호 초기화
    useEffect(() => {
        if (hasPhoneNum) {
            setInputs((prev) => {
                return {
                    ...prev,
                    phoneNum1: '',
                    phoneNum2: '',
                    phoneNum3: ''
                };
            });
        }
    }, [hasPhoneNum]);

    // 없음 선택 시 팩스 번호 초기화
    useEffect(() => {
        if (hasFaxNum) {
            setInputs((prev) => {
                return {
                    ...prev,
                    faxNum1: '',
                    faxNum2: '',
                    faxNum3: ''
                };
            });
        }
    }, [hasFaxNum]);

    // 유효성 에러 scroll 및 focus
    useEffect(() => {
        if (Object.keys(errors)[0]) {
            const input = document.getElementById(Object.keys(errors)[0]);
            input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            input?.focus();
        }
    }, [errors]);

    const onChange = useCallback(
        (e: onChangeEventType) => {
            const { name, value } = e.target;
            // 중복 & 인증 처리 해제
            if (
                (name.includes('email') || name === 'license') &&
                checkOverlap[name]
            ) {
                setCheckOverlap((prev) => {
                    return {
                        ...prev,
                        [name]: !prev[name],
                        emailAuth: name.includes('email')
                            ? !prev[name]
                            : prev[name]
                    };
                });
            }
            // 번호만
            if (name.includes('Num')) {
                return setInputs((prev) => {
                    return {
                        ...prev,
                        [name]: onlyNumber(value)
                    };
                });
            }
            // 이메일 변경 시 인증번호 초기화
            if (name === 'email') {
                return setInputs((prev) => {
                    return {
                        ...prev,
                        [name]: value,
                        emailAuthNum: ''
                    };
                });
            }
            setInputs((prev) => {
                return {
                    ...prev,
                    [name]: value
                };
            });
        },
        [checkOverlap]
    );

    // 중복확인 & 인증
    const handleCheckOverlap = (e: MouseEvent<HTMLButtonElement>) => {
        const { name } = e.currentTarget;
        try {
            setCheckOverlap((prev) => {
                return {
                    ...prev,
                    [name]: !prev[name]
                };
            });
        } catch (err) {
            console.error(err);
        }
    };

    // 약관동의
    const handleTogglePolicy = (e: onChangeEventType) => {
        const { name } = e.target;
        if (name === 'checkAll') {
            return setTogglePolicy((prev) => {
                return {
                    checkAll: !prev[name],
                    privacy: !prev[name],
                    service: !prev[name]
                };
            });
        }
        if (name === 'privacy') {
            return setTogglePolicy((prev) => {
                return {
                    ...prev,
                    [name]: !prev[name],
                    checkAll: !prev[name] && prev.service ? true : false
                };
            });
        }
        if (name === 'service') {
            return setTogglePolicy((prev) => {
                return {
                    ...prev,
                    [name]: !prev[name],
                    checkAll: !prev[name] && prev.privacy ? true : false
                };
            });
        }
    };

    const onSubmit = async () => {
        if (
            !validateJoin(
                inputs,
                checkOverlap,
                hasPhoneNum,
                hasFaxNum,
                setErrors
            )
        )
            return toast.error('정보를 다시 확인해주세요.');

        try {
            toast.success('회원가입이 신청되었습니다.');
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <A.Wrapper $margin="40px auto 150px">
            <A.Title>회원가입</A.Title>
            <StyledInput
                id="email"
                title="이메일(아이디)"
                name="email"
                value={inputs.email}
                onChange={onChange}
                placeholder="이메일을 입력해주세요"
                error={!!errors.email}
                maxLength={50}
                margin="32px 0 8px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
                disabled={checkOverlap.email && checkOverlap.emailAuthNum}
                onClick={handleCheckOverlap}
                buttonTitle="중복확인"
                buttonName="email"
                buttonDisabled={checkOverlap.email}
                WITH_BUTTON
            />
            {checkOverlap.email && (
                <StyledInput
                    id="emailAuthNum"
                    name="emailAuthNum"
                    value={inputs.emailAuthNum}
                    onChange={onChange}
                    placeholder="인증번호 확인"
                    maxLength={6}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    error={!!errors.emailAuthNum}
                    disabled={checkOverlap.email && checkOverlap.emailAuthNum}
                    onClick={handleCheckOverlap}
                    buttonTitle="인증"
                    buttonName="emailAuthNum"
                    buttonDisabled={checkOverlap.emailAuthNum}
                    WITH_BUTTON
                />
            )}
            <StyledInput
                id="username"
                title="대표자명"
                name="username"
                value={inputs.username}
                onChange={onChange}
                placeholder="이름을 입력해주세요"
                error={!!errors.username}
                maxLength={12}
                margin="20px 0"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
            />
            <StyledInput
                id="license"
                title="요양기관번호"
                name="license"
                value={inputs.license}
                onChange={onChange}
                placeholder="요양기관번호를 입력해주세요"
                error={!!errors.license}
                maxLength={8}
                margin="0 0 20px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
                onClick={handleCheckOverlap}
                buttonTitle="중복확인"
                buttonName="license"
                buttonDisabled={checkOverlap.license}
                WITH_BUTTON
            />
            <StyledInput
                id="hospitalName"
                title="병원명"
                name="hospitalName"
                value={inputs.hospitalName}
                onChange={onChange}
                placeholder="병원명을 입력해주세요"
                error={!!errors.hospitalName}
                maxLength={20}
                margin="0 0 20px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
            />
            <StyledPassword
                id="password"
                title="비밀번호"
                name="password"
                value={inputs.password}
                onChange={onChange}
                placeholder="비밀번호를 입력해주세요"
                error={!!errors.password}
                margin="0 0 8px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
                ICON={false}
            />
            <StyledPassword
                id="passwordConfirm"
                name="passwordConfirm"
                value={inputs.passwordConfirm}
                onChange={onChange}
                placeholder="비밀번호 재입력"
                error={!!errors.passwordConfirm}
                margin="0 0 20px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
                ICON={false}
            />
            <FlexBox
                $justifyContent="space-between"
                $alignItems="flex-end"
                $margin="0 0 8px"
            >
                <StyledInput
                    id="phoneNum1"
                    title="대표 전화번호"
                    name="phoneNum1"
                    value={inputs.phoneNum1}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            phoneRef1 as RefObject<HTMLInputElement>
                        );
                    }}
                    maxLength={3}
                    placeholder="선택"
                    error={!!errors.phoneNum1}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasPhoneNum}
                />
                <A.Hyphen>-</A.Hyphen>
                <StyledInput
                    id="phoneNum2"
                    name="phoneNum2"
                    value={inputs.phoneNum2}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            phoneRef2 as RefObject<HTMLInputElement>
                        );
                    }}
                    error={!!errors.phoneNum2}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasPhoneNum}
                    ref={phoneRef1}
                />
                <A.Hyphen>-</A.Hyphen>
                <StyledInput
                    id="phoneNum3"
                    name="phoneNum3"
                    value={inputs.phoneNum3}
                    onChange={onChange}
                    error={!!errors.phoneNum3}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasPhoneNum}
                    ref={phoneRef2}
                />
            </FlexBox>
            <StyledCheckbox
                checkboxId="hasPhoneNum"
                name="hasPhoneNum"
                label="없음"
                value={false}
                checked={hasPhoneNum}
                onChange={() => setHasPhoneNum((prev) => !prev)}
                margin="0 0 20px"
            />
            <FlexBox
                $justifyContent="space-between"
                $alignItems="flex-end"
                $margin="0 0 8px"
            >
                <StyledInput
                    id="faxNum1"
                    title="팩스"
                    name="faxNum1"
                    value={inputs.faxNum1}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            faxRef1 as RefObject<HTMLInputElement>
                        );
                    }}
                    error={!!errors.faxNum1}
                    maxLength={3}
                    placeholder="선택"
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasFaxNum}
                />
                <A.Hyphen>-</A.Hyphen>
                <StyledInput
                    id="faxNum2"
                    name="faxNum2"
                    value={inputs.faxNum2}
                    onChange={(e) => {
                        onChange(e);
                        autofocusHandler(
                            e,
                            faxRef2 as RefObject<HTMLInputElement>
                        );
                    }}
                    error={!!errors.faxNum2}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasFaxNum}
                    ref={faxRef1}
                />
                <A.Hyphen>-</A.Hyphen>
                <StyledInput
                    id="faxNum3"
                    name="faxNum3"
                    value={inputs.faxNum3}
                    onChange={onChange}
                    error={!!errors.faxNum3}
                    maxLength={4}
                    borderRadius={0}
                    border={`${theme.colors.lightGrayBorder} 1px solid`}
                    width={100}
                    disabled={hasFaxNum}
                    ref={faxRef2}
                />
            </FlexBox>
            <StyledCheckbox
                checkboxId="hasFaxNum"
                name="hasFaxNum"
                label="없음"
                value={false}
                checked={hasFaxNum}
                onChange={() => setHasFaxNum((prev) => !prev)}
                margin="0 0 20px"
            />
            <StyledPostcode
                id="address"
                value={inputs.address}
                setInputs={setInputs}
                error={!!errors.address}
                borderRadius={0}
                borderColor={theme.colors.lightGrayBorder}
                margin="0 0 8px"
                BUTTON
                // disabled
            />
            <StyledInput
                name="addressDetail"
                value={inputs.addressDetail}
                onChange={onChange}
                placeholder="상세주소"
                maxLength={80}
                margin="0 0 20px"
                borderRadius={0}
                border={`${theme.colors.lightGrayBorder} 1px solid`}
            />
            <StyledSelect
                id="chart"
                title="차트"
                name="chart"
                value={inputs.chart}
                options={['차트 선택', '이지스', '기타']}
                onChange={onChange}
                placeholder="차트 선택"
                error={!!errors.chart}
                margin="0 0 32px"
                borderRadius={0}
                borderColor={theme.colors.lightGrayBorder}
                COLUMN
            />
            <StyledPolicyCheckbox
                checkboxId="policyAll"
                name="checkAll"
                label="약관 전체 동의"
                subLabel="(선택항목 포함)"
                value={false}
                checked={togglePolicy.checkAll}
                onChange={handleTogglePolicy}
                margin="0 0 20px"
            />
            <StyledPolicyCheckbox
                checkboxId="policy1"
                name="privacy"
                label="[필수] 개인정보 수집·이용 동의"
                value={false}
                checked={togglePolicy.privacy}
                onChange={handleTogglePolicy}
                onClick={() => {}}
                margin="0 0 16px"
                OPTIONS
            />
            <StyledPolicyCheckbox
                checkboxId="policy2"
                name="service"
                label="[필수] 서비스 이용 약관 동의"
                value={false}
                checked={togglePolicy.service}
                onChange={handleTogglePolicy}
                onClick={() => {}}
                OPTIONS
            />
            <StyledButton
                title="회원가입하기"
                margin="32px 0 12px"
                width={343}
                fontSize={14}
                onClick={onSubmit}
                borderRadius={0}
                bgColor={theme.colors.pointBlue}
                disabled={!togglePolicy.checkAll}
            />
            <RegularFont
                $fontColor={theme.colors.deepGrayFontColor}
                $fontSize={15}
            >
                관리자 승인후 회원가입이 완료됩니다.
            </RegularFont>
        </A.Wrapper>
    );
};

export default Join;
